/**
 * Company-wide attendance rules (P2-2, P2-6, P2-7), configurable per legal entity and effective-dated.
 *
 * A legal entity may have its own version of each rule; otherwise the organisation-wide version applies; with
 * neither, the built-in behaviour applies (no late penalty, and overtime counted exactly as before these rules
 * existed). Stored in `attendance_rule_policies`, one row per version; a new version closes the previous one.
 */
import { z } from 'zod';

export const attendanceRuleKindSchema = z.enum(['late_arrival', 'overtime', 'gps_accuracy']);
export type AttendanceRuleKind = z.infer<typeof attendanceRuleKindSchema>;

/**
 * Late-arrival penalty (P2-2) with a per-cycle allowance (P2-7). The cycle is the payroll period being paid; the
 * employee's own view uses the current calendar month.
 *
 * - `count`: the first `freeLates` late days in the cycle are allowed; after that, every `everyNLates` late days
 *   cost `deductDays`. Example: free 3, every 3, 0.5 → the 6th, 9th, 12th late day each cost half a day.
 * - `minutes`: total late minutes beyond `freeMinutes` cost `deductDays` for every full `perMinutes`.
 *
 * `action`: `lop` adds the penalty to loss of pay; `none` only reports it (useful for a trial period).
 */
export const latePenaltyRuleSchema = z
  .object({
    enabled: z.boolean(),
    basis: z.enum(['count', 'minutes']),
    freeLates: z.number().int().min(0).max(31),
    everyNLates: z.number().int().min(1).max(31),
    freeMinutes: z.number().int().min(0).max(10_000),
    perMinutes: z.number().int().min(1).max(10_000),
    deductDays: z.number().min(0.25).max(5).multipleOf(0.25),
    action: z.enum(['lop', 'none']),
  })
  .strict();
export type LatePenaltyRule = z.infer<typeof latePenaltyRuleSchema>;

export const DEFAULT_LATE_PENALTY_RULE: LatePenaltyRule = Object.freeze({
  enabled: false,
  basis: 'count',
  freeLates: 3,
  everyNLates: 3,
  freeMinutes: 60,
  perMinutes: 60,
  deductDays: 0.5,
  action: 'lop',
});

/**
 * Overtime (P2-6).
 *
 * - `eligibleDayTypes`: on a working day, overtime is time beyond the shift's full-day hours; on a weekly off or
 *   holiday, every hour worked is overtime.
 * - `minMinutes`: overtime below this on a day does not count at all.
 * - `mode`: `pay` sends the hours to payroll; `comp_off` grants comp-off instead (a full day for
 *   `compOffFullDayMinutes` of overtime, half a day for half of it); `none` records the hours but neither pays nor
 *   credits them.
 * - `monthlyCapHours`: the most overtime paid in one payroll period; null = no cap.
 * - `requiresApproval`: overtime counts only after a manager approves the day.
 */
export const overtimeRuleSchema = z
  .object({
    enabled: z.boolean(),
    eligibleDayTypes: z.array(z.enum(['working', 'weekly_off', 'holiday'])).min(1),
    minMinutes: z.number().int().min(0).max(720),
    mode: z.enum(['pay', 'comp_off', 'none']),
    compOffFullDayMinutes: z.number().int().min(60).max(1440),
    monthlyCapHours: z.number().min(0).max(500).nullable(),
    requiresApproval: z.boolean(),
  })
  .strict();
export type OvertimeRule = z.infer<typeof overtimeRuleSchema>;

/** The form's starting point. Saving it is a choice; until a company saves one, overtime behaves as before. */
export const DEFAULT_OVERTIME_RULE: OvertimeRule = Object.freeze({
  enabled: true,
  eligibleDayTypes: ['working', 'weekly_off', 'holiday'] as OvertimeRule['eligibleDayTypes'],
  minMinutes: 0,
  mode: 'pay',
  compOffFullDayMinutes: 480,
  monthlyCapHours: null,
  requiresApproval: false,
});

/**
 * GPS accuracy limit. A phone reports how far off its position may be; a punch whose reported accuracy is worse
 * than `maxAccuracyM` metres is FLAGGED `low_accuracy` on the punch. It is never refused and never notifies
 * anyone: the flag only lets HR filter for it. Until a company saves one the limit is 100 m.
 */
export const gpsAccuracyRuleSchema = z
  .object({
    maxAccuracyM: z.number().int().min(10).max(5000),
  })
  .strict();
export type GpsAccuracyRule = z.infer<typeof gpsAccuracyRuleSchema>;

export const DEFAULT_GPS_ACCURACY_RULE: GpsAccuracyRule = Object.freeze({ maxAccuracyM: 100 });

export const attendanceRulePolicyDtoSchema = z.object({
  id: z.string().uuid(),
  kind: attendanceRuleKindSchema,
  /** Null = the organisation-wide version. */
  legalEntityId: z.string().uuid().nullable(),
  /** A late-arrival rule for one department or one employee (a Person id); null otherwise. The most
   *  specific rule wins for a person: their own, their department's, their entity's, the org's. */
  departmentId: z.string().uuid().nullable().optional(),
  personId: z.string().uuid().nullable().optional(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  config: z.union([latePenaltyRuleSchema, overtimeRuleSchema, gpsAccuracyRuleSchema]),
  createdAt: z.string(),
});
export type AttendanceRulePolicyDto = z.infer<typeof attendanceRulePolicyDtoSchema>;

export const saveAttendanceRulePolicyBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('late_arrival'),
    legalEntityId: z.string().uuid().nullable(),
    /** For one department only (then `legalEntityId` and `personId` are left empty). */
    departmentId: z.string().uuid().nullable().optional(),
    /** For one employee only, by Person id (then the other two scopes are left empty). */
    personId: z.string().uuid().nullable().optional(),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    config: latePenaltyRuleSchema,
  }),
  z.object({
    kind: z.literal('overtime'),
    legalEntityId: z.string().uuid().nullable(),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    config: overtimeRuleSchema,
  }),
  z.object({
    kind: z.literal('gps_accuracy'),
    legalEntityId: z.string().uuid().nullable(),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    config: gpsAccuracyRuleSchema,
  }),
]).superRefine((body, ctx) => {
  // A late-arrival rule has exactly one reach: the company, one legal entity, one department or one employee.
  if (body.kind !== 'late_arrival') return;
  if ([body.legalEntityId, body.departmentId, body.personId].filter(Boolean).length > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A rule applies to the company, one legal entity, one department or one employee',
      path: ['departmentId'],
    });
  }
});
export type SaveAttendanceRulePolicyBody = z.infer<typeof saveAttendanceRulePolicyBodySchema>;
